<template>
    <v-container class="pa-5" fluid>
        <PriceListDialog
            :is-open="priceListDialog"
            :price-list-name="priceListName"
            @on-save="getMaterials()"
            @on-close="closePriceListDialog"
        />
        <SaveMaterialDialog
            :is-open="saveMaterialDialog"
            :material="material"
            :product-production-properties="productProductionProperties"
            @on-save="saveMaterial"
            @on-close="closeSaveMaterialDialog"
        />

        <v-row align="center" dense>
            <v-col cols="auto">
                <page-title>Inne materiały</page-title>
            </v-col>
            <v-col cols="auto">
                <SelectMenu />
            </v-col>
        </v-row>
        <v-row align="center">
            <v-col cols="12" lg="4">
                <v-btn
                    color="secondary"
                    medium
                    data-sentry-label="Dodaj materiał"
                    @click="showCreateMaterialDialog"
                >
                    <v-icon left>
                        mdi-plus
                    </v-icon>
                    Dodaj materiał
                </v-btn>
            </v-col>

            <v-col cols="12" lg="8">
                <v-container fluid class="pa-0">
                    <v-row justify="end" dense>
                        <v-col sm="auto">
                            <v-checkbox
                                v-model="filters.isVisible"
                                label="Ukryj nieaktywne"
                                hide-details
                                class="mt-2"
                                @change="changeFilter"
                            />
                        </v-col>
                        <v-col cols="12" sm="3">
                            <v-text-field
                                v-model="filters.name"
                                append-icon="mdi-magnify"
                                label="Nazwa..."
                                single-line
                                clearable
                                hide-details
                                solo
                                @keyup.enter="changeFilter"
                                @click:append="changeFilter"
                                @click:clear="clearFilter('name')"
                            />
                        </v-col>
                        <v-col cols="12" sm="3">
                            <v-text-field
                                v-model="filters.materialIndex"
                                append-icon="mdi-magnify"
                                label="Indeks materiałowy..."
                                single-line
                                clearable
                                hide-details
                                solo
                                @keyup.enter="changeFilter"
                                @click:append="changeFilter"
                                @click:clear="clearFilter('materialIndex')"
                            />
                        </v-col>
                    </v-row>
                </v-container>
            </v-col>
        </v-row>

        <v-row v-if="canImportExportFile" justify="end" no-gutters>
            <v-col cols="auto">
                <v-btn
                    text
                    x-small
                    data-sentry-label="Generuj raport"
                    :disabled="generateMaterialsPriceListLoading"
                    @click="generateMaterialsPriceList"
                >
                    <v-icon
                        v-if="!generateMaterialsPriceListLoading"
                        left
                        small
                        color="green"
                    >
                        mdi-file-excel
                    </v-icon>
                    <v-progress-circular
                        v-else
                        indeterminate
                        color="primary"
                        class="mr-1"
                        size="12"
                        width="2"
                    />
                    Generuj cennik
                </v-btn>
            </v-col>
            <v-col cols="auto">
                <v-btn
                    text
                    x-small
                    data-sentry-label="Importuj cennik"
                    @click="showPriceListDialog"
                >
                    <v-icon
                        small
                        color="primary"
                        left
                    >
                        mdi-file-excel
                    </v-icon>
                    Importuj cennik
                </v-btn>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12">
                <v-card>
                    <v-data-table
                        :headers="headers"
                        :items="materials"
                        :loading="materialsLoading"
                        :footer-props="footerProps"
                        :page.sync="pagination.page"
                        :server-items-length="pagination.itemsLength"
                        :mobile-breakpoint="0"
                        name="materialsData"
                        @pagination="updatePagination"
                    >
                        <template #item.materialType="{item}">
                            {{ item.materialType.name }}
                        </template>
                        <template #item.unitOfMeasure="{item}">
                            {{ item.unitOfMeasure.name }}
                        </template>
                        <template #item.isVisible="{item}">
                            <v-icon
                                v-if="item.isVisible"
                                small
                                color="success"
                            >
                                mdi-check
                            </v-icon>
                            <v-icon v-else small color="error">
                                mdi-close
                            </v-icon>
                        </template>
                        <template #item.piecePrice="{item}">
                            {{ formatPrice(item.piecePrice) }}
                        </template>
                        <template #item.actions="{item}">
                            <div class="d-inline-flex">
                                <v-tooltip :open-delay="tooltipDelay" top>
                                    <template #activator="{on}">
                                        <v-btn
                                            icon
                                            v-on="on"
                                            @click="showEditMaterialDialog(item.id)"
                                        >
                                            <v-icon color="teal">
                                                mdi-pencil
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Edytuj</span>
                                </v-tooltip>
                                <v-tooltip :open-delay="tooltipDelay" top>
                                    <template #activator="{on}">
                                        <v-btn
                                            icon
                                            v-on="on"
                                            @click="hideMaterial(item.id)"
                                        >
                                            <v-icon color="pink">
                                                mdi-eye-off
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Ukryj</span>
                                </v-tooltip>
                            </div>
                        </template>
                    </v-data-table>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import PageTitle from '@/components/common/PageTitle'
import filters from '@/mixins/dataTableFilters'
import SaveMaterialDialog from './SaveMaterialDialog'
import pagination from '@/mixins/dataTablePagination'
import tooltipDelay from '@/mixins/tooltip'
import priceFormatter from '@/mixins/priceFormatter'
import { errorHandler } from '@/utils/errorHandler'
import { hasUserPermission } from '@/utils/permissionsHandler'
import SelectMenu from '@/views/Configurations/Materials/Common/SelectMenu'
import PriceListDialog from '@/views/Configurations/Materials/Common/PriceListDialog'

export default {
    name: 'OtherMaterials',
    components: {
        PageTitle, SaveMaterialDialog, SelectMenu, PriceListDialog
    },
    mixins: [
        filters,
        pagination,
        tooltipDelay,
        priceFormatter
    ],
    beforeRouteLeave(to, from, next) {
        this.$store.dispatch('configurations/clearMaterialsFilters')
        next()
    },
    data() {
        return {
            material: {},
            productProductionProperties: [],
            saveMaterialDialog: false,
            generateMaterialsPriceListLoading: false,
            user: null,
            priceListName: null,
            priceListDialog: null,
            headers: [
                {
                    text: 'Nazwa',
                    value: 'name',
                    sortable: false
                },
                {
                    text: 'Indeks Materiałowy',
                    value: 'materialIndex',
                    sortable: false,
                },
                {
                    text: 'Typ materiału',
                    value: 'materialType',
                    sortable: false,
                },
                {
                    text: 'Cena netto za jednostkę (zł)',
                    value: 'piecePrice',
                    sortable: false,
                    align: 'center',
                    width: 120
                },
                {
                    text: 'Jednostka',
                    value: 'unitOfMeasure',
                    sortable: false,
                    width: 100
                },
                {
                    text: 'Czy aktywny?',
                    value: 'isVisible',
                    sortable: false,
                    align: 'center',
                    width: 80
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    sortable: false,
                    align: 'center',
                    width: 100
                }
            ]
        }
    },
    computed: {
        userId() {
            return this.$store.state.user.userId
        },
        materials() {
            return this.$store.state.configurations.materials.materials
        },
        materialsLoading() {
            return this.$store.state.configurations.materials.materialsLoading
        },
        canImportExportFile() {
            return hasUserPermission('materials_price_update')
        },
        savedFilters() {
            return this.$store.state.configurations.materials.filters
        },
        filterQuery() {
            const filters = Object.assign({}, this.filters)
            let query = ''

            for (let item in filters) {
                if (filters[item] != null && filters[item] != '') {
                    if (!(item === 'isVisible' && filters[item] === false)) {
                        query = query.concat('&', `${item}=${filters[item]}`)
                    }
                }
            }

            return query
        },
    },
    mounted() {
        this.getMaterials(),
        Promise.all([
            this.$store.dispatch('configurations/getProductProductionProperties'),
            this.$store.dispatch('configurations/getUnitsOfMeasure'),
            this.$store.dispatch('configurations/getMaterialTypes'),
            this.$store.dispatch('administration/getUser', this.userId )
        ])
            .then(responses => {
                this.productProductionProperties = responses[0].data.results
                this.user = responses[3].data
            })
            .catch(error => {
                errorHandler(error)
            })
    },
    methods: {
        getMaterials() {
            this.$store.dispatch('configurations/getMaterials', {
                paginate: this.paginate(),
                query: this.filterQuery
            })
                .then(response => {
                    this.handlePaginationResponse(response)
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        showCreateMaterialDialog() {
            this.saveMaterialDialog = true
        },
        showEditMaterialDialog(materialId) {
            this.$store.dispatch('configurations/getMaterial', materialId)
                .then(response => {
                    this.material = response.data
                    this.saveMaterialDialog = true
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        closeSaveMaterialDialog() {
            this.saveMaterialDialog = false

            setTimeout(() => {
                this.material = {}
            }, 500)
        },
        saveMaterial() {
            this.saveMaterialDialog = false
            this.getMaterials()
            this.closeSaveMaterialDialog()
        },
        hideMaterial(materialId) {
            this.$store.dispatch('configurations/updateMaterial', {
                id: materialId,
                isVisible: false
            })
                .then(() => {
                    this.getMaterials()
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        generateMaterialsPriceList() {
            this.generateMaterialsPriceListLoading = true
            this.$store.dispatch('configurations/generateMaterialsPriceList', {
                userEmail: this.user.email
            })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.generateMaterialsPriceListLoading = false
                })
        },
        showPriceListDialog() {
            this.priceListDialog = true
            this.priceListName = 'materials'
        },
        closePriceListDialog() {
            this.priceListDialog = false
        },
        changeFilter() {
            this.$store.dispatch('configurations/setMaterialsFilters', this.filters)
            this.resetPagination()
            this.getMaterials()
        },
        updatePagination(pagination) {
            this.pagination = pagination

            if (pagination.pageCount !== 0 && !this.paginationSettings.paginationPending) {
                this.getMaterials()
            }
            this.clearPaginationPending()
        }
    }
}
</script>
